import { DataTable, DataTableProps } from 'mantine-datatable';
import { CSSProperties } from 'react';

import { ZorroLoader } from '../Loader';
import { ZorroStack } from '../Stack';
import { ZorroText } from '../Text';
import checkboxClassNames from '../v2/Checkbox/Checkbox.module.css';
import classNames from './DataTable.module.scss';
import { ZorroDataTableActionCell } from './DataTableActionCell';
import { DataTablePagination } from './DataTablePagination';
import { EmptyIcon } from './EmptyIcon';

const EMPTY_TABLE_HEIGHT = 250;

/**
 * Standard row height for list/report tables. Matches the natural height of a
 * row whose tallest cell holds a 30px pill (as in the enrollments table):
 * 30px of content + 2 x 10px (`--mantine-spacing-xs`) of vertical padding.
 */
export const DATATABLE_ROW_HEIGHT = '3.125rem';

// DataTableProps is a union (columns or groups); a plain Omit would merge it.
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type IDataTableProps<T = any> = DistributiveOmit<
  DataTableProps<T>,
  'fetching' | 'pinFirstColumn'
> & {
  isSelectable?: boolean;
  shouldPinFirstColumn?: boolean;
  reduceHeightBy?: number;
  shouldGrow?: boolean;
  isFetching?: boolean;
  // When true, the table stretches to fill its container width (instead of
  // shrinking to content) and header cells stay on a single line.
  shouldFillWidth?: boolean;
  // Minimum height for body rows, e.g. DATATABLE_ROW_HEIGHT. Rows still grow
  // past it when a cell is taller; the extra space reads as vertical padding.
  rowHeight?: string | number;
};

// TODO: move svg to own component.
export function ZorroDataTable({
  withTableBorder = true,
  withRowBorders = true,
  records = [],
  height: heightProp = records?.length === 0 ? EMPTY_TABLE_HEIGHT : 'unset',
  minHeight = 150,
  horizontalSpacing = 'md',
  verticalSpacing = 'sm',
  withColumnBorders = true,
  noRecordsText = 'No records to show',
  emptyState = undefined,
  shouldPinFirstColumn = true,
  // reduceHeightBy is deprecated: grow now fills the table's flex parent.
  // Destructured here so it's not spread to mantine-datatable.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  reduceHeightBy = 270,
  isSelectable = false,
  isFetching = false,
  groups,
  shouldGrow,
  shouldFillWidth = false,
  rowHeight,
  sortStatus = { columnAccessor: '', direction: 'asc' },
  onSortStatusChange,
  ...props
}: IDataTableProps) {
  // Merge the fill-width class straight into the table's className so it lands
  // on the <table> element regardless of how mantine-datatable forwards props.
  const tableClassNames = shouldFillWidth
    ? { ...classNames, table: `${classNames.table} ${classNames.fillWidth}` }
    : classNames;

  const rowHeightStyle = rowHeight
    ? ({
        '--zorro-datatable-row-height':
          typeof rowHeight === 'number' ? `${rowHeight}px` : rowHeight,
      } as CSSProperties)
    : undefined;

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <DataTable
      {...props}
      // Array form keeps any caller-provided `style` (which may be a
      // theme callback) intact alongside the row-height variable.
      style={[props.style, rowHeightStyle]}
      withTableBorder={withTableBorder}
      withRowBorders={withRowBorders}
      records={records}
      groups={groups}
      height={shouldGrow ? '100%' : heightProp}
      // Don't set a minHeight when we have data, but add default minHeight of 150px for empty state
      minHeight={records?.length > 0 ? undefined : minHeight}
      borderRadius="md"
      horizontalSpacing={horizontalSpacing}
      verticalSpacing={verticalSpacing}
      withColumnBorders={withColumnBorders}
      rowClassName={classNames.row}
      data-is-selectable={isSelectable}
      data-pin-first-column={shouldPinFirstColumn}
      data-has-groups={groups && groups.length > 0}
      scrollAreaProps={{ type: 'always', ...props.scrollAreaProps }}
      customRowAttributes={() => ({
        'data-is-selectable': isSelectable,
        'data-pin-first-column': shouldPinFirstColumn,
      })}
      renderPagination={(ctx: {
        state: Parameters<typeof DataTablePagination>[0]['state'];
        actions: Parameters<typeof DataTablePagination>[0]['actions'];
      }) => (
        <DataTablePagination
          state={ctx.state}
          actions={ctx.actions}
          recordsPerPageOptions={props.recordsPerPageOptions}
          paginationText={props.paginationText}
        />
      )}
      classNames={tableClassNames}
      emptyState={
        records?.length === 0 ? (
          <div style={{ pointerEvents: 'auto' }}>
            {emptyState || (
              <ZorroStack gap="xs">
                <EmptyIcon />
                <ZorroText>{noRecordsText}</ZorroText>
              </ZorroStack>
            )}
          </div>
        ) : (
          <div />
        )
      }
      noRecordsIcon={undefined}
      fetching={isFetching}
      customLoader={<ZorroLoader />}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      // for aligning the checkbox of select row
      selectionColumnClassName="data-selection-column"
      selectionCheckboxProps={{
        unstyled: true,
        classNames: checkboxClassNames,
      }}
      allRecordsSelectionCheckboxProps={{
        unstyled: true,
        classNames: checkboxClassNames,
      }}
      getRecordSelectionCheckboxProps={() => ({
        unstyled: true,
        classNames: checkboxClassNames,
      })}
    />
  );
}

ZorroDataTable.ActionCell = ZorroDataTableActionCell;
